/**
 * Message List Component
 *
 * Scrollable list of messages with loading and empty states.
 */

import { useEffect, useRef } from 'react';
import { MessageBubble } from './MessageBubble';
import type { WidgetMessage, Labels } from '../types/widget';

const ChatBubbleIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lc-w-12 lc-h-12 lc-text-gray-300"
    >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

const BotIcon = ({ color }: { color: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lc-w-4 lc-h-4"
    >
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <circle cx="12" cy="5" r="2" />
        <path d="M12 7v4" />
        <line x1="8" y1="16" x2="8" y2="16" strokeWidth="3" strokeLinecap="round" />
        <line x1="16" y1="16" x2="16" y2="16" strokeWidth="3" strokeLinecap="round" />
    </svg>
);

function getInitials(name: string): string {
    return name
        .split(' ')
        .map((w) => w.charAt(0))
        .slice(0, 2)
        .join('')
        .toUpperCase();
}

interface MessageListProps {
    messages: WidgetMessage[];
    isLoading: boolean;
    labels: Labels;
    primaryColor?: string;
    isBotTyping?: boolean;
    agentName?: string;
    chatMode?: 'bot' | 'agent';
}

export function MessageList({
                                messages,
                                isLoading,
                                labels,
                                primaryColor = '#FF3E6C',
                                isBotTyping = false,
                                agentName,
                                chatMode = 'bot',
                            }: MessageListProps) {
    const listRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages.length, isBotTyping]);

    if (isLoading) {
        return (
            <div className="lc-flex-1 lc-overflow-y-auto lc-p-4 lc-flex lc-flex-col lc-gap-3 lc-bg-gray-50">
                <div className="lc-flex lc-items-center lc-justify-center lc-h-full lc-text-gray-500">
                    <div
                        className="lc-w-6 lc-h-6 lc-border-2 lc-border-gray-200 lc-rounded-full lc-animate-spin lc-ml-2"
                        style={{ borderTopColor: primaryColor }}
                    />
                    <span className="lc-text-sm">{labels.connecting}</span>
                </div>
            </div>
        );
    }

    if (!messages.length) {
        return (
            <div
                className="lc-flex-1 lc-overflow-y-auto lc-p-5 lc-flex lc-items-center lc-justify-center lc-bg-gray-50"
                style={{
                    background: `radial-gradient(circle at top, color-mix(in srgb, ${primaryColor} 10%, transparent), transparent 45%), #f9fafb`,
                }}
            >
                <div className="lc-w-full lc-max-w-sm lc-text-center lc-flex lc-flex-col lc-items-center">
                    <div
                        className="lc-w-16 lc-h-16 lc-rounded-2xl lc-flex lc-items-center lc-justify-center lc-mb-4 lc-border lc-shadow-sm"
                        style={{
                            backgroundColor: `color-mix(in srgb, ${primaryColor} 10%, white)`,
                            borderColor: `color-mix(in srgb, ${primaryColor} 22%, white)`,
                            boxShadow: `0 12px 32px color-mix(in srgb, ${primaryColor} 14%, transparent)`,
                        }}
                    >
                        <BotIcon color={primaryColor} />
                    </div>
    
                    <div
                        className="lc-inline-flex lc-items-center lc-gap-1.5 lc-rounded-full lc-px-3 lc-py-1 lc-text-[11px] lc-font-medium lc-mb-3"
                        style={{
                            color: primaryColor,
                            backgroundColor: `color-mix(in srgb, ${primaryColor} 9%, white)`,
                        }}
                    >
                        <span
                            className="lc-w-1.5 lc-h-1.5 lc-rounded-full"
                            style={{ backgroundColor: primaryColor }}
                        />
                        AI זמין עכשיו
                    </div>
    
                    <h3 className="lc-text-lg lc-font-bold lc-text-gray-900 lc-mb-2">
                        היי, איך אפשר לעזור?
                    </h3>
    
                    <p className="lc-text-sm lc-leading-6 lc-text-gray-500 lc-mb-4">
                        אפשר לשאול אותי כל שאלה, ואני אענה לפי המידע שהוגדר במערכת.
                    </p>
    
                    <div className="lc-flex lc-flex-wrap lc-justify-center lc-gap-2">
                        {[
                            'מה השירותים שלכם?',
                            'איך יוצרים קשר?',
                            'אני רוצה נציג',
                        ].map((item) => (
                            <button
                                key={item}
                                type="button"
                                className="lc-rounded-full lc-border lc-bg-white lc-px-3 lc-py-1.5 lc-text-xs lc-font-medium lc-text-gray-600 lc-shadow-sm lc-transition hover:lc--translate-y-0.5"
                                style={{
                                    borderColor: `color-mix(in srgb, ${primaryColor} 18%, #e5e7eb)`,
                                }}
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const typingDisplayName =
        agentName || (chatMode === 'agent' ? 'נציג' : 'AI');

    return (
        <div
            ref={listRef}
            className="lc-flex-1 lc-overflow-y-auto lc-p-4 lc-flex lc-flex-col lc-gap-3 lc-bg-gray-50 lc-scroll-smooth"
        >
            {messages.map((msg) => (
                <MessageBubble
                    key={msg.id}
                    message={msg}
                    primaryColor={primaryColor}
                    agentName={agentName}
                />
            ))}

            {isBotTyping && chatMode === 'bot' && (
                <div className="lc-flex lc-justify-end lc-w-full lc-gap-2 lc-items-end">
                    <div
                        className="lc-w-8 lc-h-8 lc-rounded-full lc-flex lc-items-center lc-justify-center lc-flex-shrink-0 lc-mb-0.5"
                        style={{ backgroundColor: `${primaryColor}20` }}
                    >
                        <BotIcon color={primaryColor} />
                    </div>

                    <div className="lc-max-w-[75%] lc-flex lc-flex-col lc-items-end">
                        <div className="lc-flex lc-items-center lc-gap-1 lc-mb-1 lc-pr-1">
                            <span className="lc-text-[11px] lc-text-gray-500">{typingDisplayName}</span>
                            <span
                                className="lc-text-[10px] lc-font-medium lc-px-1.5 lc-py-0.5 lc-rounded"
                                style={{ color: primaryColor, backgroundColor: `${primaryColor}18` }}
                            >
                AI
              </span>
                        </div>

                        <div className="lc-px-4 lc-py-2.5 lc-rounded-2xl lc-rounded-bl-sm lc-bg-white lc-shadow-sm lc-flex lc-items-center lc-gap-2">
              <span className="lc-text-[11px] lc-text-gray-400">
                {typingDisplayName} מקליד...
              </span>
                            <div className="lc-flex lc-items-center lc-gap-1">
                                <span className="lc-typing-dot" />
                                <span className="lc-typing-dot lc-delay-100" />
                                <span className="lc-typing-dot lc-delay-200" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isBotTyping && chatMode === 'agent' && (
                <div className="lc-flex lc-justify-end lc-w-full lc-gap-2 lc-items-end">
                    <div className="lc-w-8 lc-h-8 lc-rounded-full lc-bg-emerald-100 lc-flex lc-items-center lc-justify-center lc-text-emerald-700 lc-text-[11px] lc-font-bold lc-flex-shrink-0 lc-mb-0.5 lc-border lc-border-emerald-200">
                        {getInitials(typingDisplayName)}
                    </div>

                    <div className="lc-max-w-[75%] lc-flex lc-flex-col lc-items-end">
                        <div className="lc-flex lc-items-center lc-gap-1 lc-mb-1 lc-pr-1">
                            <span className="lc-text-[11px] lc-text-gray-500">{typingDisplayName}</span>
                            <span className="lc-text-[10px] lc-font-medium lc-px-1.5 lc-py-0.5 lc-rounded lc-bg-emerald-50 lc-text-emerald-700">
                נציג
              </span>
                        </div>

                        <div className="lc-px-4 lc-py-2.5 lc-rounded-2xl lc-rounded-bl-sm lc-bg-emerald-50 lc-shadow-sm lc-flex lc-items-center lc-gap-2 lc-border lc-border-emerald-100">
              <span className="lc-text-[11px] lc-text-emerald-700">
                {typingDisplayName} מקליד...
              </span>
                            <div className="lc-flex lc-items-center lc-gap-1">
                                <span className="lc-typing-dot" />
                                <span className="lc-typing-dot lc-delay-100" />
                                <span className="lc-typing-dot lc-delay-200" />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}